import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Logo from '@/components/base/Logo';
import { supabase } from '@/lib/supabase';

type Estado = 'verificando' | 'ok' | 'expirado' | 'invalido';

/**
 * Destino do link enviado por e-mail (ADR-0014).
 *
 * Pública de propósito: quem clica costuma abrir o link em outro navegador, ou
 * no celular, sem sessão. O token é a prova de posse — exigir login aqui
 * transformaria a confirmação num segundo obstáculo, que é justamente o que
 * este fluxo existe para evitar.
 */
export default function VerificarEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [estado, setEstado] = useState<Estado>('verificando');

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setEstado('invalido');
      return;
    }

    let cancelado = false;

    (async () => {
      const { data, error } = await supabase.functions.invoke('verificar-email', {
        body: { token },
      });

      if (cancelado) return;

      const corpo = data as { verificado?: boolean; expirado?: boolean } | null;

      if (corpo?.verificado) setEstado('ok');
      else if (corpo?.expirado) setEstado('expirado');
      else if (error || corpo) setEstado('invalido');
      else setEstado('invalido');
    })();

    return () => {
      cancelado = true;
    };
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-50 px-4">
      <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
        <div className="mb-6 flex justify-center">
          <Logo />
        </div>

        {estado === 'verificando' && <p className="text-gray-600">Confirmando seu e-mail…</p>}

        {estado === 'ok' && (
          <>
            <h1 className="mb-2 text-xl font-semibold text-gray-900">E-mail confirmado</h1>
            <p className="mb-6 text-gray-600">
              Pronto. Agora você consegue recuperar o acesso se esquecer a senha.
            </p>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="rounded-md bg-gray-900 px-4 py-2 font-medium text-white hover:bg-gray-800"
            >
              Ir para o painel
            </button>
          </>
        )}

        {estado === 'expirado' && (
          <>
            <h1 className="mb-2 text-xl font-semibold text-gray-900">Link expirado</h1>
            <p className="mb-6 text-gray-600">
              Entre na sua conta e use <span className="font-medium">Reenviar e-mail</span> no aviso
              do topo para receber um link novo.
            </p>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="rounded-md bg-gray-900 px-4 py-2 font-medium text-white hover:bg-gray-800"
            >
              Entrar
            </button>
          </>
        )}

        {estado === 'invalido' && (
          <>
            <h1 className="mb-2 text-xl font-semibold text-gray-900">Link inválido</h1>
            <p className="mb-6 text-gray-600">
              Este link não vale mais — ele funciona uma vez só. Se você já confirmou, não precisa
              fazer nada.
            </p>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="rounded-md bg-gray-900 px-4 py-2 font-medium text-white hover:bg-gray-800"
            >
              Entrar
            </button>
          </>
        )}
      </div>
    </div>
  );
}
